import React, { useState } from "react";
import { Control, UseFormSetValue, useWatch } from "react-hook-form";
import {
  DateController,
  Icons,
  ReactSelectController,
  Text,
} from "@pushpress/shared-components";
import CheckTime from "../../../components/controllers/CheckTime";
import { AppointmentForm } from "./BookAppointmentDrawer.types";
import { intervalOptions } from "./BookAppointmentDrawer.utils";
import RecursionButton, { RecursionData } from "./RecursionButton";

interface SeriesProps {
  control: Control<AppointmentForm>;
  daysErrorMessage?: string;
  setValue: UseFormSetValue<AppointmentForm>;
}

function Series(props: SeriesProps) {
  const { control, daysErrorMessage, setValue } = props;

  const [endDate, count, days] = useWatch({
    control,
    name: ["endDate", "count", "days"],
  });

  const [lastTimeTyped, setLastTimeTyped] = useState<string>(() => {
    const enabled = Object.keys(days).find((key) => days[key].enabled);
    return enabled ? days[enabled]?.time : undefined;
  });

  const handleRecurringChange = (data: RecursionData) => {
    setValue("count", data.occurrences);
    setValue("endDate", data.endDate);
  };

  const handleToggle = (
    day: keyof AppointmentForm["days"],
    checked: boolean,
  ) => {
    if (days[day].time) {
      return;
    }
    setValue(`days.${day}`, {
      enabled: checked,
      time: lastTimeTyped,
    });
  };

  const daysHasError = !!daysErrorMessage;

  return (
    <>
      <DateController
        control={control}
        name="startDate"
        label="Start series"
        required
        startIcon={Icons.Classes}
        autoComplete="off"
      />
      <ReactSelectController
        control={control}
        name="interval"
        label="Interval"
        hideLabel
        required
        options={intervalOptions}
      />
      <CheckTime
        control={control}
        label="Sun"
        name="days.su.time"
        checkboxName="days.su.enabled"
        checkboxWidth="80px"
        required
        error={daysHasError}
        onValueChange={setLastTimeTyped}
        onToggle={(checked) => handleToggle("su", checked)}
      />
      <CheckTime
        control={control}
        label="Mon"
        name="days.mo.time"
        checkboxName="days.mo.enabled"
        checkboxWidth="80px"
        required
        error={daysHasError}
        onValueChange={setLastTimeTyped}
        onToggle={(checked) => handleToggle("mo", checked)}
      />
      <CheckTime
        control={control}
        label="Tue"
        name="days.tu.time"
        checkboxName="days.tu.enabled"
        checkboxWidth="80px"
        required
        error={daysHasError}
        onValueChange={setLastTimeTyped}
        onToggle={(checked) => handleToggle("tu", checked)}
      />
      <CheckTime
        control={control}
        label="Wed"
        name="days.we.time"
        checkboxName="days.we.enabled"
        checkboxWidth="80px"
        required
        error={daysHasError}
        onValueChange={setLastTimeTyped}
        onToggle={(checked) => handleToggle("we", checked)}
      />
      <CheckTime
        control={control}
        label="Thu"
        name="days.th.time"
        checkboxName="days.th.enabled"
        checkboxWidth="80px"
        required
        error={daysHasError}
        onValueChange={setLastTimeTyped}
        onToggle={(checked) => handleToggle("th", checked)}
      />
      <CheckTime
        control={control}
        label="Fri"
        name="days.fr.time"
        checkboxName="days.fr.enabled"
        checkboxWidth="80px"
        required
        error={daysHasError}
        onValueChange={setLastTimeTyped}
        onToggle={(checked) => handleToggle("fr", checked)}
      />
      <CheckTime
        control={control}
        label="Sat"
        name="days.sa.time"
        checkboxName="days.sa.enabled"
        checkboxWidth="80px"
        required
        error={daysHasError}
        onValueChange={setLastTimeTyped}
        onToggle={(checked) => handleToggle("sa", checked)}
      />
      {daysErrorMessage && (
        <Text color="wild-watermellon-600" className="mb-3">
          {daysErrorMessage}
        </Text>
      )}
      <RecursionButton
        data={{ endDate, occurrences: count }}
        onChange={handleRecurringChange}
      />
    </>
  );
}

export default Series;
